'use client';

import { motion } from 'framer-motion';

interface DemoEmailRowProps {
  from: string;
  subject: string;
  time: string;
  folderLabel?: string;
  folderColor?: string;
  folderBgColor?: string;
  folderBorderColor?: string;
  score?: number;
  scoreLabel?: string;
  dimmed?: boolean;
  avatarColor?: string;
}

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-purple-500',
  'bg-green-500',
  'bg-pink-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-amber-500',
  'bg-rose-500',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function DemoEmailRow({
  from,
  subject,
  time,
  folderLabel,
  folderColor,
  folderBgColor,
  folderBorderColor,
  score,
  scoreLabel,
  dimmed = false,
  avatarColor,
}: DemoEmailRowProps) {
  const color = avatarColor ?? getAvatarColor(from);
  const initial = from.charAt(0).toUpperCase();

  return (
    <motion.div
      className={`flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.03] px-3 py-2.5 transition-opacity ${
        dimmed ? 'opacity-40' : 'opacity-100'
      }`}
      layout
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${color}`}
      >
        {initial}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-white/90">{from}</span>
          <span className="shrink-0 text-xs text-white/40">{time}</span>
        </div>
        <p className="truncate text-sm text-white/50">{subject}</p>
      </div>

      {score !== undefined && scoreLabel && (
        <motion.div
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-red-500/15 px-2.5 py-1 text-xs font-medium text-red-400"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          <span className="tabular-nums">{score}</span>
          <span className="text-red-400/60">|</span>
          <span>{scoreLabel}</span>
        </motion.div>
      )}

      {folderLabel && (
        <motion.div
          className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${folderColor ?? 'text-white/60'} ${folderBgColor ?? 'bg-white/5'} ${folderBorderColor ?? 'border-white/10'}`}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          {folderLabel}
        </motion.div>
      )}
    </motion.div>
  );
}
